var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useCallback } from 'react';
import { jwtDecode } from '@rad/utility/parsers';
import { addRequestInterceptor, addResponseInterceptor } from '@rad/utility/http';
import { getWebSocketManager } from '@rad/utility/websocket';
import { createCookieStorage } from '@rad/utility/cookie-storage';
import { useSocketEvent, SOCKET_EVENTS } from '../common/socket';
import { refreshToken, logoutUser } from '../common/network/auth';
import APPSETTINGS from '@appsettings';
const CLAIM_EMAIL = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress';
const storage = createCookieStorage({
    'domain': APPSETTINGS.APP_DOMAIN,
    'sameSite': 'Strict',
});
const emptyAuth = {
    'token': null,
    'decoded': {},
    'firstName': '',
    'lastName': '',
    'email': '',
    'phone': '',
    'hasPermission': () => false,
    'isRegistered': false,
    'isAuthenticated': false,
    'isSubscribed': () => false,
    'logout': (f) => f(),
};
let removeJwtInterceptor = () => { };
let remove401Interceptor = () => { };
const setAuthHeader = (token) => {
    removeJwtInterceptor();
    removeJwtInterceptor = addRequestInterceptor((init) => (Object.assign(Object.assign({}, init), { headers: Object.assign(Object.assign({}, (init.headers || {})), { Authorization: `Bearer ${token}` }) })));
};
const setup401Interceptor = (onUnauthorized) => {
    remove401Interceptor();
    remove401Interceptor = addResponseInterceptor((res) => {
        if (res.status === 401) {
            onUnauthorized();
        }
        return res;
    });
};
export const ContextAuth = React.createContext(undefined);
const integrateContext = (context) => context ? {
    hasPermission: (x) => x.some(p => context.permissions.includes(p)),
    isSubscribed: (x) => x === undefined ? !!context.subscription : context.subscription === x,
} : {
    'hasPermission': () => false,
    'isSubscribed': () => false,
};
/* TODO:
  - inject context *refresh* logic - currently relies on app-root-level `useEffect` on `token`
  - untangle `setAuth` reference inside `parseToken..logout`
  - [re]move websocket stuff? separate concerns
 */
export const useAuthState = (context) => {
    const wsManagerRef = React.useRef(getWebSocketManager());
    const clearUser = (f) => {
        storage.removeItem('userToken');
        removeJwtInterceptor();
        remove401Interceptor();
        wsManagerRef.current.disconnect();
        f === null || f === void 0 ? void 0 : f();
        window.location.reload();
    };
    const parseToken = useCallback((context) => {
        const jwt = storage.getItem('userToken');
        if (jwt !== null) {
            try {
                setAuthHeader(jwt);
                setup401Interceptor(clearUser);
                const decoded = jwtDecode(jwt);
                // Console log JWT updates
                console.log('[Auth] JWT loaded:', {
                    userId: decoded.ID,
                    isRegistered: decoded.isRegistered,
                    firstName: decoded.firstName,
                    lastName: decoded.lastName,
                });
                // Connect WebSocket with token
                wsManagerRef.current.connect(jwt);
                //half of this return is decoded, Auth and Decoded should be more similar. Create Auth as a composition of Decoded
                return Object.assign(Object.assign({ 'token': jwt, 'decoded': decoded, 'firstName': decoded.firstName, 'lastName': decoded.lastName, 'email': decoded[CLAIM_EMAIL], 'phone': decoded.phone, 'isAuthenticated': true, 'isRegistered': decoded.isRegistered.toLowerCase() === 'true' }, integrateContext(context)), { 'logout': (f) => {
                        logoutUser().catch(() => { });
                        clearUser(() => {
                            setAuth(emptyAuth);
                            f();
                        });
                    } });
            }
            catch (_a) {
                storage.removeItem('userToken');
                return emptyAuth;
            }
        }
        else {
            return emptyAuth;
        }
    }, []);
    const [stateAuth, setAuth] = useState(() => parseToken(context));
    useSocketEvent(SOCKET_EVENTS.AUTH_REFRESH, (payload) => __awaiter(void 0, void 0, void 0, function* () {
        console.log('[Auth] Received auth refresh:', payload.Reason);
        const [success, data] = yield refreshToken();
        if (success) {
            storage.setItem('userToken', data.token);
            setAuth(parseToken(data.context));
        }
        else {
            console.error('[Auth] Failed to refresh token:', data);
        }
    }));
    return [
        stateAuth,
        (jwt, context) => {
            storage.setItem('userToken', jwt);
            setAuth(parseToken(context));
        },
        (context) => {
            setAuth(parseToken(context));
        },
    ];
};
export const useAuthO = () => {
    const context = React.useContext(ContextAuth);
    if (!context) {
        throw new Error('useAuth - no provider');
    }
    return {
        'auth': context.state,
        'setToken': context.setToken,
        'setContext': context.setContext,
    };
};
export const useAuth = () => {
    const { auth, setToken } = useAuthO();
    return [auth, setToken];
};
export default useAuth;
